// ----------------------------------------------------------------------

import { IReadLegalProvision } from '@raas/shared/src/types/legal-provision';

const EntityIdHeader = 'Entity-Id';
const VersionIdHeader = 'Version-Id';
const VersionTimestampHeader = 'Version-Timestamp';
const ReleaseTimestampHeader = 'Release-Timestamp';
export const LegalProvisionIdHeader = 'Legal-Provision-Id';
export const BaseDocumentElementIdHeader = 'Base-Document-Element-Id';

export const BASE_HEADERS = {
  EntityId: EntityIdHeader,
  VersionId: VersionIdHeader,
  VersionTimestamp: VersionTimestampHeader,
  ReleaseTimestamp: ReleaseTimestampHeader,
  LegalProvisionId: LegalProvisionIdHeader,
};

const CompareEntityIdHeader = 'Compare-Entity-Id';
const CompareVersionIdHeader = 'Compare-Version-Id';
const CompareVersionTimestampHeader = 'Compare-Version-Timestamp';
const CompareReleaseTimestampHeader = 'Compare-Release-Timestamp';
const CompareLegalProvisionIdHeader = 'Compare-Legal-Provision-Id';

export const COMPARE_HEADERS = {
  CompareEntityId: CompareEntityIdHeader,
  CompareVersionId: CompareVersionIdHeader,
  CompareVersionTimestamp: CompareVersionTimestampHeader,
  CompareReleaseTimestamp: CompareReleaseTimestampHeader,
  CompareLegalProvisionId: CompareLegalProvisionIdHeader,
};

function paths(root: string, sublink: string, parameters: string) {
  return `${root}${sublink}${parameters}`;
}

function path(root: string, sublink: string) {
  return `${root}${sublink}`;
}

function buildContextParameters(legalProvisionContext: IReadLegalProvision) {
  const params = {
    [EntityIdHeader]: legalProvisionContext?.entityId,
    [VersionIdHeader]: legalProvisionContext?.versionId,
    [VersionTimestampHeader]: legalProvisionContext?.versionTimestamp?.toISOString(),
    [ReleaseTimestampHeader]: legalProvisionContext?.releaseTimestamp?.toISOString(),
    // it's required for the edit document element page to work
    [LegalProvisionIdHeader]: legalProvisionContext?.id,
  };

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      searchParams.append(key, value);
    }
  });

  return `?${searchParams.toString()}`;

  // return `?${EntityIdHeader}=${legalProvisionContext?.entityId}&${VersionIdHeader}=${
  //   legalProvisionContext?.versionId
  // }&${VersionTimestampHeader}=${legalProvisionContext?.versionTimestamp?.toISOString()}&${ReleaseTimestampHeader}=${legalProvisionContext?.releaseTimestamp?.toISOString()}`;
}

export type BuildContextParametersProps = {
  id: string;
  entityId: string;
  versionId: string;
  versionTimestamp: Date;
  releaseTimestamp?: Date;
};

export type ContextParameters = {
  [EntityIdHeader]: string;
  [VersionIdHeader]: string;
  [VersionTimestampHeader]: string;
  [ReleaseTimestampHeader]?: string;
  [LegalProvisionIdHeader]: string;
};

function buildContextParametersForRequests({
  id,
  entityId,
  versionId,
  versionTimestamp,
  releaseTimestamp,
}: BuildContextParametersProps) {
  let params: ContextParameters = {
    [EntityIdHeader]: entityId,
    [VersionIdHeader]: versionId,
    [VersionTimestampHeader]: versionTimestamp.toISOString(),
    // it's required for the edit document element page to work
    [LegalProvisionIdHeader]: id,
  };

  if (releaseTimestamp) {
    params = {
      ...params,
      [ReleaseTimestampHeader]: releaseTimestamp?.toISOString(),
    };
  }

  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      searchParams.append(key, value);
    }
  });
  return `?${searchParams.toString()}`;
}

function updateSearchParams(params: Record<string, string | undefined>, search?: string) {
  const searchParams = new URLSearchParams(search ?? window.location.search);
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      searchParams.set(key, value);
    } else {
      searchParams.delete(key);
    }
  });
  return searchParams;
}

export function addBaseContextParameters(base: IReadLegalProvision, search?: string) {
  const params = {
    [LegalProvisionIdHeader]: base?.id,
    [EntityIdHeader]: base?.entityId,
    [VersionIdHeader]: base?.versionId,
    [VersionTimestampHeader]: base?.versionTimestamp?.toISOString(),
    [ReleaseTimestampHeader]: base?.releaseTimestamp?.toISOString(),
  };
  return `?${updateSearchParams(params, search).toString()}`;
}

export function addCompareContextParameters(compare: IReadLegalProvision, search?: string) {
  const params = {
    [CompareLegalProvisionIdHeader]: compare?.id,
    [CompareEntityIdHeader]: compare?.entityId,
    [CompareVersionIdHeader]: compare?.versionId,
    [CompareVersionTimestampHeader]: compare?.versionTimestamp?.toISOString(),
    [CompareReleaseTimestampHeader]: compare?.releaseTimestamp?.toISOString(),
  };
  return `?${updateSearchParams(params, search).toString()}`;
}

export function buildCompareContextParameters(
  base: IReadLegalProvision,
  compare: IReadLegalProvision,
  search?: string
) {
  let searchParams = new URLSearchParams(search ?? window.location.search);
  // Add base parameters
  searchParams = updateSearchParams(
    {
      [LegalProvisionIdHeader]: base?.id,
      [EntityIdHeader]: base?.entityId,
      [VersionIdHeader]: base?.versionId,
      [VersionTimestampHeader]: base?.versionTimestamp?.toISOString(),
      [ReleaseTimestampHeader]: base?.releaseTimestamp?.toISOString(),
    },
    searchParams.toString()
  );
  // Add compare parameters
  searchParams = updateSearchParams(
    {
      [CompareLegalProvisionIdHeader]: compare?.id,
      [CompareEntityIdHeader]: compare?.entityId,
      [CompareVersionIdHeader]: compare?.versionId,
      [CompareVersionTimestampHeader]: compare?.versionTimestamp?.toISOString(),
      [CompareReleaseTimestampHeader]: compare?.releaseTimestamp?.toISOString(),
    },
    searchParams.toString()
  );
  return `?${searchParams.toString()}`;
}

// ----------------------------------------------------------------------
export const ROOTS_DASHBOARD = '/dashboard';

export const PATH_AUTH = {
  login: '/login',
};

export const PATH_SIGNUP = {
  root: '/signup',
};

export const PATH_DASHBOARD = {
  root: ROOTS_DASHBOARD,

  overview: {
    root: path(ROOTS_DASHBOARD, '/overview'),
  },

  legalProvision: {
    root: path(ROOTS_DASHBOARD, '/legal-provision'),
    details: (id: string, legalProvisionContext: IReadLegalProvision, queryHash: string = null) => {
      const hash = queryHash ? `#${queryHash}` : '';
      return paths(
        ROOTS_DASHBOARD,
        `/legal-provision/${id}/details${hash}`,
        buildContextParameters(legalProvisionContext)
      );
    },
    new: path(ROOTS_DASHBOARD, '/legal-provision/new'),
    edit: (id: string, legalProvisionContext: IReadLegalProvision) =>
      paths(
        ROOTS_DASHBOARD,
        `/legal-provision/${id}/edit`,
        buildContextParameters(legalProvisionContext)
      ),
    newVersion: (id: string, legalProvisionContext: IReadLegalProvision) =>
      paths(
        ROOTS_DASHBOARD,
        `/legal-provision/${id}/new`,
        buildContextParameters(legalProvisionContext)
      ),
    manageAccess: (entityId: string, legalProvisionContext: IReadLegalProvision) =>
      paths(
        ROOTS_DASHBOARD,
        `/legal-provision/${entityId}/manage-access`,
        buildContextParameters(legalProvisionContext)
      ),
    translation: (
      id: string,
      legalProvisionContext: IReadLegalProvision,
      queryHash: string = null
    ) => {
      const hash = queryHash ? `#${queryHash}` : '';
      return paths(
        ROOTS_DASHBOARD,
        `/legal-provision/${id}/translation${hash}`,
        buildContextParameters(legalProvisionContext)
      );
    },
  },

  documentElement: {
    new: path(ROOTS_DASHBOARD, '/document-element/new'),
    edit: (id: string, legalProvisionContext: IReadLegalProvision) =>
      paths(
        ROOTS_DASHBOARD,
        `/document-element/${id}/edit`,
        buildContextParameters(legalProvisionContext)
      ),
    view: (id: string, legalProvisionContext: IReadLegalProvision) =>
      paths(
        ROOTS_DASHBOARD,
        `/document-element/${id}/view`,
        buildContextParameters(legalProvisionContext)
      ),
  },

  sentenceFragments: {
    root: (id: string) => path(ROOTS_DASHBOARD, `/sentence-fragments/${id}`),
  },
  legalRequirement: {
    root: path(ROOTS_DASHBOARD, '/legal-requirement'),
    new: path(ROOTS_DASHBOARD, '/legal-requirement/new'),
    edit: (id: string, legalProvisionContext: IReadLegalProvision) =>
      paths(
        ROOTS_DASHBOARD,
        `/legal-requirement/${id}/edit`,
        buildContextParameters(legalProvisionContext)
      ),
  },

  property: {
    root: path(ROOTS_DASHBOARD, '/property'),
    new: path(ROOTS_DASHBOARD, '/property/new'),
    edit: (id: string, legalProvisionContext: IReadLegalProvision) =>
      paths(ROOTS_DASHBOARD, `/property/${id}/edit`, buildContextParameters(legalProvisionContext)),
  },
  radar: {
    root: path(ROOTS_DASHBOARD, '/legal-radar'),
  },
  technicalRequirement: {
    root: path(ROOTS_DASHBOARD, '/technical-requirement'),
    new: path(ROOTS_DASHBOARD, '/technical-requirement/new'),
    edit: (id: string, legalProvisionContext: IReadLegalProvision) =>
      paths(
        ROOTS_DASHBOARD,
        `/technical-requirement/${id}/edit`,
        buildContextParameters(legalProvisionContext)
      ),
  },
  input: {
    root: path(ROOTS_DASHBOARD, '/input'),
    new: path(ROOTS_DASHBOARD, '/input/new'),
    edit: (id: string, legalProvisionContext: IReadLegalProvision) =>
      paths(ROOTS_DASHBOARD, `/input/${id}/edit`, buildContextParameters(legalProvisionContext)),
  },

  tagGroup: {
    root: path(ROOTS_DASHBOARD, '/tag-group'),
    new: path(ROOTS_DASHBOARD, '/tag-group/new'),
    edit: (id: string) => path(ROOTS_DASHBOARD, `/tag-group/${id}/edit`),
  },

  tag: {
    root: path(ROOTS_DASHBOARD, '/tag'),
    new: path(ROOTS_DASHBOARD, '/tag/new'),
    edit: (id: string) => path(ROOTS_DASHBOARD, `/tag/${id}/edit`),
  },

  release: {
    new: (id: string, versionId: string, legalProvisionContext: IReadLegalProvision) =>
      paths(
        ROOTS_DASHBOARD,
        `/release/${id}/${versionId}/new`,
        buildContextParameters(legalProvisionContext)
      ),
  },

  user: {
    changePassword: path(ROOTS_DASHBOARD, '/user/change-password'),
    profile: path(ROOTS_DASHBOARD, '/user/profile'),
    signUp: path(ROOTS_DASHBOARD, '/user/sign-up'),
  },

  delta: {
    root: path(ROOTS_DASHBOARD, '/delta'),
    legalProvisions: (
      base: IReadLegalProvision | null = null,
      compare: IReadLegalProvision | null = null
    ) => {
      if (base && compare) {
        return paths(ROOTS_DASHBOARD, `/delta/lp`, buildCompareContextParameters(base, compare));
      }
      return path(ROOTS_DASHBOARD, `/delta/lp`);
    },
    documentElement: (
      documentElementId: string,
      base: IReadLegalProvision | null = null,
      compare: IReadLegalProvision | null = null
    ) => {
      if (base && compare) {
        return paths(
          ROOTS_DASHBOARD,
          `/delta/de/moving`,
          buildCompareContextParameters(
            base,
            compare,
            `${BaseDocumentElementIdHeader}=${documentElementId}`
          )
        );
      }
      return path(ROOTS_DASHBOARD, `/delta/de/moving/${documentElementId}`);
    },
  },

  exports: {
    list: path(ROOTS_DASHBOARD, '/exports/list'),
    new: path(ROOTS_DASHBOARD, '/exports/new'),
    edit: (id: string) => path(ROOTS_DASHBOARD, `/exports/${id}/edit`),
  },
  reports: {
    list: path(ROOTS_DASHBOARD, '/reports/list'),
  },
  import: path(ROOTS_DASHBOARD, '/import'),
  alerts: {
    list: path(ROOTS_DASHBOARD, '/alerts/list'),
    new: path(ROOTS_DASHBOARD, '/alerts/new'),
    edit: (id: string) => path(ROOTS_DASHBOARD, `/alerts/${id}/edit`),
  },
  translation: path(ROOTS_DASHBOARD, '/translation/current-legal-provision'),
  regulation: {
    root: path(ROOTS_DASHBOARD, '/regulation'),
    tree: (params: BuildContextParametersProps, queryHash: string = null) => {
      const hash = queryHash ? `#${queryHash}` : '';
      return paths(
        ROOTS_DASHBOARD,
        `/regulation/${params?.id}/tree${hash}`,
        buildContextParametersForRequests({ ...params })
      );
    },
    properties: (params: BuildContextParametersProps) =>
      paths(
        ROOTS_DASHBOARD,
        `/regulation/${params?.id}/properties`,
        buildContextParametersForRequests({ ...params })
      ),
    content: (params: BuildContextParametersProps, queryHash: string = null) => {
      const hash = queryHash ? `#${queryHash}` : '';
      return paths(
        ROOTS_DASHBOARD,
        `/regulation/${params?.id}/content${hash}`,
        buildContextParametersForRequests({ ...params })
      );
    },
    access: (params: BuildContextParametersProps, queryHash: string = null) => {
      const hash = queryHash ? `#${queryHash}` : '';
      return paths(
        ROOTS_DASHBOARD,
        `/regulation/${params?.id}/access${hash}`,
        buildContextParametersForRequests({ ...params })
      );
    },
  },
  admins: {
    root: path(ROOTS_DASHBOARD, '/admins'),
  },
  integration: {
    root: path(ROOTS_DASHBOARD, '/integration/search'),
    settings: path(ROOTS_DASHBOARD, '/integration/settings'),
  },
  subscription: {
    create: path(ROOTS_DASHBOARD, '/subscription/create'),
    edit: (id: string) => path(ROOTS_DASHBOARD, `/subscription/${id}/edit`),
  },
  bundling: {
    root: path(ROOTS_DASHBOARD, '/bundlings'),
    list: path(ROOTS_DASHBOARD, '/bundlings/list'),
    new: path(ROOTS_DASHBOARD, '/bundlings/new'),
    edit: (id: string) => path(ROOTS_DASHBOARD, `/bundlings/${id}/edit`),
  },
  attributes: {
    root: path(ROOTS_DASHBOARD, '/attributes'),
    list: path(ROOTS_DASHBOARD, '/attributes/list'),
    new: path(ROOTS_DASHBOARD, '/attributes/new'),
    edit: (id: string) => path(ROOTS_DASHBOARD, `/attributes/${id}/edit`),
  },
  access: {
    root: path(ROOTS_DASHBOARD, '/access'),
    list: path(ROOTS_DASHBOARD, '/access/list'),
    newAccessRegulation: path(ROOTS_DASHBOARD, '/access/new'),
    newRegulation: ({
      organizationId,
      userGroupId,
    }: {
      organizationId: string;
      userGroupId: string;
    }) =>
      path(
        ROOTS_DASHBOARD,
        `/access/new?organizationId=${organizationId}&userGroupId=${userGroupId}`
      ),
    edit: (id: string) => path(ROOTS_DASHBOARD, `/access/${id}/edit`),
  },
  organization: {
    root: path(ROOTS_DASHBOARD, '/organization'),
    list: path(ROOTS_DASHBOARD, '/organization/list'),
    new: path(ROOTS_DASHBOARD, '/organization/new'),
    item: (id: string) => path(ROOTS_DASHBOARD, `/organization/${id}`),
    edit: (id: string) => path(ROOTS_DASHBOARD, `/organization/${id}/edit`),
    groups: (id: string) => path(ROOTS_DASHBOARD, `/organization/${id}/group/list`),
    newGroup: (id: string) => path(ROOTS_DASHBOARD, `/organization/${id}/group/new`),
    groupItem: (id: string, groupId: string) =>
      path(ROOTS_DASHBOARD, `/organization/${id}/group/${groupId}`),
    groupMembers: (id: string, groupId: string) =>
      path(ROOTS_DASHBOARD, `/organization/${id}/group/${groupId}/member/list`),
    groupRegulations: (id: string, groupId: string) =>
      path(ROOTS_DASHBOARD, `/organization/${id}/group/${groupId}/regulation/list`),
    members: (id: string) => path(ROOTS_DASHBOARD, `/organization/${id}/member/list`),
    newMember: ({ organizationId }: { organizationId: string }) =>
      path(ROOTS_DASHBOARD, `/organization/new-member?organizationId=${organizationId}`),
    newGroupMember: ({
      organizationId,
      organizationGroupId,
    }: {
      organizationId: string;
      organizationGroupId: string;
    }) =>
      path(
        ROOTS_DASHBOARD,
        `/organization/new-group-member?organizationId=${organizationId}&organizationGroupId=${organizationGroupId}`
      ),
  },
};
